import React, { useEffect, useState } from "react";
import { useAvatarCache } from "../../../contexts/AvatarContext";

interface AvatarProps {
    userId: string;
    displayName?: string;
    className?: string;
    color?: string;
}

/* 🎨 deterministic colors */
const colors = [
    "bg-red-500",
    "bg-green-500",
    "bg-blue-500",
    "bg-purple-500",
    "bg-yellow-500",
    "bg-pink-500",
    "bg-indigo-500",
];

const getColor = (name: string) =>
    colors[name.charCodeAt(0) % colors.length];

const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0]?.toUpperCase();
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const UserAvatar: React.FC<AvatarProps> = ({
    userId,
    displayName,
    className = "w-8 h-8",
    color,
}) => {
    const { getAvatarUrl } = useAvatarCache();
    const [src, setSrc] = useState<string | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const resolveBgColor = (name?: string, color?: string) => {
        if (color) return color;
        return getColor(name || "?");
    };

    const isHexColor = (value?: string) =>
        !!value && value.startsWith("#");

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const url = await getAvatarUrl(userId);
                if (active) setSrc(url || null);
            } catch {
                if (active) setSrc(null);
            }
        };

        load();
        return () => {
            active = false;
        };
    }, [userId, getAvatarUrl]);

    /* IMAGE RENDER */
    if (src) {
        return (
            <div
                className={`rounded-full overflow-hidden flex-shrink-0 ${className}`}
                title={displayName}
            >
                <img
                    src={src}
                    alt={displayName || "avatar"}
                    className="w-full h-full object-cover block"
                    onError={() => setSrc(null)}
                />
            </div>
        );
    }

    /* INITIALS FALLBACK */
    return (
        <div
            className="relative flex-shrink-0"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <div
                className={`rounded-full flex items-center justify-center text-white text-xs font-medium
    ${!isHexColor(color) ? resolveBgColor(displayName || userId, color) : ""}
    ${className}`}
                style={isHexColor(color) ? { backgroundColor: color } : undefined}
            >
                {getInitials(displayName || userId)}
            </div>

            {showTooltip && displayName && (
                <div
                    className="absolute -top-9 left-[180%] -translate-x-1/2 
          whitespace-nowrap rounded bg-white px-2 py-1 text-xs 
          text-black shadow-md max-w-28 truncate"
                >
                    {displayName}
                </div>
            )}
        </div>
    );
};

export default UserAvatar;
